import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { API } from "@/App";
import { School, Mail, Lock, Eye, EyeOff, ChevronRight, Building2, ArrowLeft, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import { toast } from "sonner";

export default function SchoolLogin() {
  const navigate = useNavigate();
  const [step, setStep] = useState("login"); // login, otp
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Form data
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [schoolCode, setSchoolCode] = useState("");
  const [otp, setOtp] = useState("");
  
  // OTP info
  const [otpEmail, setOtpEmail] = useState("");

  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!email || !password || !schoolCode) {
      toast.error("Please fill in all fields");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (data.requires_otp) {
        // 2FA flow (currently disabled)
        setOtpEmail(data.otp_sent_to || "school email");
        setStep("otp");
        toast.success(`OTP sent to ${data.otp_sent_to || "school email"}`);
      } else if (response.ok && data.success) {
        // Direct login (2FA disabled)
        toast.success("Login successful!");
        navigate("/school/dashboard");
      } else if (!response.ok) {
        toast.error(data.error || data.detail || "Login failed");
      }
    } catch (error) {
      console.error("Login error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    
    if (!otp || otp.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          otp,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        toast.success("Login successful!");
        navigate("/school/dashboard");
      } else {
        toast.error(data.detail || "Invalid OTP");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    setLoading(true);
    
    try {
      const response = await fetch(`${API}/school/admin/resend-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          email: email.trim().toLowerCase(),
          password,
          school_code: schoolCode.trim().toUpperCase()
        }),
      });
      
      const data = await response.json();
      
      if (data.requires_otp) {
        toast.success("OTP resent successfully");
      } else {
        toast.error(data.detail || "Failed to resend OTP");
      }
    } catch (error) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 flex flex-col">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100">
        <SharedHeader transparent={true} />
      </header>

      {/* Background Pattern */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-emerald-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-teal-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 w-80 h-80 bg-cyan-200 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-blob animation-delay-4000"></div>
      </div>

      <div className="flex-1 flex items-center justify-center p-4">
        <div className="relative w-full max-w-md">

        <Card className="border-0 shadow-2xl bg-white/80 backdrop-blur-xl">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg mb-4">
              <School className="w-8 h-8 text-white" />
            </div>
            <CardTitle className="text-2xl font-bold text-slate-800">School Portal</CardTitle>
            <CardDescription className="text-slate-600">
              {step === "login" ? "Sign in to your school admin account" : "Enter the OTP sent to your school email"}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {step === "login" ? (
              <form onSubmit={handleLogin} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="schoolCode" className="text-slate-700">School Code</Label>
                  <div className="relative">
                    <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="schoolCode"
                      type="text"
                      placeholder=""
                      value={schoolCode}
                      onChange={(e) => setSchoolCode(e.target.value.toUpperCase())}
                      className="pl-10 uppercase"
                      data-testid="school-code-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email" className="text-slate-700">Email</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="email"
                      type="email"
                      placeholder=""
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="pl-10"
                      data-testid="school-email-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="password" className="text-slate-700">Password</Label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder=""
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="pl-10 pr-10"
                      data-testid="school-password-input"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  disabled={loading}
                  data-testid="school-login-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Signing in...
                    </span>
                  ) : (
                    <span className="flex items-center gap-2">
                      Continue
                      <ChevronRight size={16} />
                    </span>
                  )}
                </Button>
              </form>
            ) : (
              <form onSubmit={handleVerifyOTP} className="space-y-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-4">
                  <p className="text-sm text-emerald-700">
                    A 6-digit verification code has been sent to <strong>{otpEmail}</strong>
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="otp" className="text-slate-700">Verification Code</Label>
                  <div className="relative">
                    <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                    <Input
                      id="otp"
                      type="text"
                      placeholder=""
                      value={otp}
                      onChange={(e) => setOtp(e.target.value.replace(/\D/g, "").slice(0, 6))}
                      className="pl-10 text-center text-2xl tracking-[0.5em] font-mono"
                      maxLength={6}
                      data-testid="school-otp-input"
                    />
                  </div>
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  disabled={loading || otp.length !== 6}
                  data-testid="school-verify-otp-button"
                >
                  {loading ? (
                    <span className="flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                      Verifying...
                    </span>
                  ) : (
                    "Verify & Login"
                  )}
                </Button>

                <div className="flex items-center justify-between text-sm">
                  <button
                    type="button"
                    onClick={() => {
                      setStep("login");
                      setOtp("");
                    }}
                    className="text-slate-600 hover:text-slate-800"
                  >
                    Back to login
                  </button>
                  <button
                    type="button"
                    onClick={handleResendOTP}
                    disabled={loading}
                    className="text-emerald-600 hover:text-emerald-700 font-medium"
                  >
                    Resend OTP
                  </button>
                </div>
              </form>
            )}
          </CardContent>

          <CardFooter className="flex flex-col gap-4 pt-0">
            <div className="w-full border-t border-slate-200 pt-4">
              <p className="text-center text-sm text-slate-600">
                New school?{" "}
                <Link to="/school/register" className="text-emerald-600 hover:text-emerald-700 font-medium">
                  Register your school
                </Link>
              </p>
            </div>
          </CardFooter>
        </Card>

        {/* Info */}
        <p className="text-center text-xs text-slate-500 mt-6">
          For B2B school partnerships. Contact us at{" "}
          <a href="mailto:info@contentiumtech.com" className="text-emerald-600 hover:underline">
            info@contentiumtech.com
          </a>
        </p>
        </div>
      </div>

      {/* Footer */}
      <SharedFooter />
    </div>
  );
}
